import { useState } from 'react';
import { FileText } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { ZoneSignature } from './ZoneSignature';
import { useSigner } from '../../lib/useSignatures';
import { ErreurApi } from '../../lib/api';

export interface SignataireEnAttente {
  id: string;
  nom: string;
  roleLibelle: string;
  signeLe: string | null;
}

/**
 * Recueil d'une signature sur place, sur un document versé au dossier : la
 * tablette reste sur l'écran, on la tend au signataire, la ligne se met à jour
 * derrière la fenêtre. Le document se lit avant de se signer — c'est sa copie
 * au dossier, celle dont la demande a pris l'empreinte.
 */
export function ModaleSignatureDocument({
  libelle,
  urlLecture,
  demandeSignatureId,
  signataires,
  empreinteDocument,
  onFermer,
}: {
  libelle: string;
  urlLecture: string | null;
  demandeSignatureId: string;
  signataires: SignataireEnAttente[];
  empreinteDocument: string | null;
  onFermer: () => void;
}): JSX.Element {
  const signer = useSigner();
  const restants = signataires.filter((signataire) => !signataire.signeLe);
  const [choisi, setChoisi] = useState<SignataireEnAttente | null>(
    restants.length === 1 ? (restants[0] ?? null) : null,
  );

  return (
    <Modale titre="Signature sur tablette" sousTitre={libelle} ouverte onFermer={onFermer}>
      {urlLecture && (
        <div className="px-4 pt-4 sm:px-6">
          <a
            href={urlLecture}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-[44px] items-center gap-2 rounded-xl border border-codialis-sky/30 bg-codialis-sand px-4 text-sm font-semibold text-codialis-navy transition hover:bg-white"
          >
            <FileText className="h-4 w-4 shrink-0" aria-hidden />
            Lire le document avant de signer
          </a>
        </div>
      )}
      {!choisi ? (
        <div className="space-y-2 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">Qui signe maintenant ?</p>
          {restants.map((signataire) => (
            <button
              key={signataire.id}
              type="button"
              onClick={() => setChoisi(signataire)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              {signataire.nom}
              <span className="ml-1 font-normal text-codialis-gray">({signataire.roleLibelle})</span>
            </button>
          ))}
        </div>
      ) : (
        <ZoneSignature
          libelle={libelle}
          signataire={choisi}
          empreinteDocument={empreinteDocument}
          enCours={signer.isPending}
          erreur={signer.error instanceof ErreurApi ? signer.error.message : null}
          resultat={signer.data ? { mention: signer.data.mention, scellee: signer.data.scellee } : null}
          onAnnuler={onFermer}
          onSigner={(trace) =>
            signer.mutate(
              { demandeId: demandeSignatureId, signataireId: choisi.id, trace },
              { onSuccess: onFermer },
            )
          }
        />
      )}
    </Modale>
  );
}
